import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, DatePicker, Table, Tag, Typography, Space, Button, message, Modal, Form, Select, InputNumber, Input, Switch, Tooltip, Radio, Popconfirm } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import { ReloadOutlined, CalendarOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { teacherWeeklyScheduleService } from '../services/teacherWeeklyScheduleService';
import type { TeacherWeeklySchedule } from '../types/teacherWeeklySchedule';
import courseService from '../services/courseService';
import { getSubjectsByCourse } from '../services/subjectService';
import roomService from '../services/roomService';
import nhomThucHanhService from '../services/nhomThucHanhService';
import { buildAPIURL } from '../config/apiConfig';
import { AuthService } from '../services/authService';
import type { RoomDto } from '../services/roomService';
import type { Course } from '../../../shared/types';
import api from '../utils/api';

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

const { Title, Text } = Typography;

type LessonType = 'theory' | 'practice';

interface PracticeGroupOption {
  value: string;
  label: string;
  classSectionId?: string;
  raw?: any;
}

interface TeacherOption {
  value: string;
  label: string;
  raw?: any;
}

const TeacherWeeklySchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState<TeacherWeeklySchedule[]>([]);
  const [weekDate, setWeekDate] = useState<Dayjs>(dayjs());
  const [range, setRange] = useState<{ start: string; end: string } | undefined>(undefined);
  const [teacherOptions, setTeacherOptions] = useState<TeacherOption[]>([]);
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | undefined>(undefined);

  const [modalState, setModalState] = useState<{ visible: boolean; mode: 'create' | 'edit'; record?: TeacherWeeklySchedule | null }>({
    visible: false,
    mode: 'create',
    record: null
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form] = Form.useForm();

  const [courseOptions, setCourseOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [courseLoading, setCourseLoading] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [classSectionOptions, setClassSectionOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [classSectionLoading, setClassSectionLoading] = useState(false);
  const [roomOptions, setRoomOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [roomLoading, setRoomLoading] = useState(false);
  const [practiceGroupOptions, setPracticeGroupOptions] = useState<PracticeGroupOption[]>([]);
  const [practiceGroupLoading, setPracticeGroupLoading] = useState(false);
  const [activatingId, setActivatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const selectedCourseId = Form.useWatch('khoaHocId', form);
  const selectedSubjectId = Form.useWatch('monHocId', form);
  const lessonType: LessonType = (Form.useWatch('lessonType', form) as LessonType | undefined) ?? 'theory';

  const modalVisible = modalState.visible;
  const isEditMode = modalState.mode === 'edit';
  const editingRecord = modalState.record ?? null;
  const authUser = useMemo(() => AuthService.getUser(), []);
  const isAdminUser = useMemo(() => {
    const roleKey = String(authUser?.RoleKey ?? (authUser as any)?.roleKey ?? '').toLowerCase();
    return roleKey.includes('admin');
  }, [authUser]);

  const teacherLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    teacherOptions.forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, [teacherOptions]);

  const fetchSchedules = useCallback(async (reference: Dayjs, teacherId?: string) => {
    if (!teacherId) {
      setSchedules([]);
      setRange(undefined);
      return;
    }

    setLoading(true);
    try {
      const start = reference.startOf('week').format('YYYY-MM-DD');
      const end = reference.endOf('week').format('YYYY-MM-DD');
      const response = await teacherWeeklyScheduleService.list({ weekStart: start, weekEnd: end, teacherId });
      setSchedules(response.data);
      setRange(response.range ?? { start, end });
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Không thể tải lịch giảng tuần';
      message.error(messageText);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeachers = useCallback(async () => {
    setTeacherLoading(true);
    try {
      const response = await api.get('/api/v1/users/teachers');
      const data = Array.isArray(response.data?.data) ? response.data.data : Array.isArray(response.data) ? response.data : [];
      const options: TeacherOption[] = data.map((teacher: any) => {
        const username = teacher.Username || teacher.username || '';
        const userId = teacher.UserId != null ? String(teacher.UserId) : undefined;
        const value = username || userId || '';
        const displayName = teacher.FullName || teacher.fullName || username || userId || '';
        const label = username ? `${displayName} (${username})` : displayName;
        return {
          value,
          label,
          raw: teacher
        };
      });

      const validOptions = options.filter((option: TeacherOption) => Boolean(option.value));

      setTeacherOptions(validOptions);

      if (!selectedTeacherId && validOptions.length) {
        const currentUser = AuthService.getUser();
        const preferredValue = currentUser?.Username || (currentUser?.UserId ? String(currentUser.UserId) : undefined);
        const matched = preferredValue ? validOptions.find((option) => option.value === preferredValue) : undefined;
        setSelectedTeacherId(matched ? matched.value : validOptions[0].value);
      }
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Không thể tải danh sách giảng viên';
      message.error(messageText);
      setTeacherOptions([]);
    } finally {
      setTeacherLoading(false);
    }
  }, [selectedTeacherId]);

  const loadCourses = useCallback(async () => {
    setCourseLoading(true);
    try {
      const response = await courseService.getCourses({ page: 1, limit: 200 });
      const data = Array.isArray(response?.data) ? response.data : [];
      const options = data.map((course: Course) => ({
        value: course.KhoaHocID,
        label: `${course.KhoaHocID} - ${course.TenKhoaHoc}`
      }));
      setCourseOptions(options);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Không thể tải danh sách khóa học';
      message.error(messageText);
      setCourseOptions([]);
    } finally {
      setCourseLoading(false);
    }
  }, []);

  const loadRooms = useCallback(async () => {
    setRoomLoading(true);
    try {
      const response = await roomService.list({ page: 1, limit: 200 });
      const rooms = Array.isArray(response?.data) ? response.data : [];
      const options = rooms.map((room: RoomDto) => ({
        value: room.PhongHocID,
        label: room.TenPhongHoc || room.PhongHocID
      }));
      setRoomOptions(options);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Không thể tải danh sách phòng học';
      message.error(messageText);
      setRoomOptions([]);
    } finally {
      setRoomLoading(false);
    }
  }, []);

  const loadSubjects = useCallback(async (courseId: string) => {
    if (!courseId) {
      setSubjectOptions([]);
      return;
    }
    setSubjectLoading(true);
    try {
      const subjects = await getSubjectsByCourse(courseId);
      const subjectList = Array.isArray(subjects) ? (subjects as any[]) : [];
      const options = subjectList.map((subject) => ({
        value: String(subject?.MonHocID ?? subject?.SubjectId ?? ''),
        label: `${subject?.MonHocID ?? subject?.SubjectId ?? ''} - ${subject?.TenMonHoc ?? ''}`.trim()
      }));
      setSubjectOptions(options);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Không thể tải danh sách môn học';
      message.error(messageText);
      setSubjectOptions([]);
    } finally {
      setSubjectLoading(false);
    }
  }, []);

  const loadClassSections = useCallback(async (subjectId: string) => {
    if (!subjectId) {
      setClassSectionOptions([]);
      return;
    }
    setClassSectionLoading(true);
    try {
      const token = AuthService.getToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(buildAPIURL(`/lophocphan/by-monhoc/${encodeURIComponent(subjectId)}`), { headers });
      const payload = await response.json();
      const data = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      const options = data.map((section: any) => ({
        value: section.LopHocPhanID ?? section.MaLHP,
        label: `${section.LopHocPhanID ?? section.MaLHP} - ${section.TenLHP ?? section.TenLopHP ?? section.TenLopHocPhan ?? ''}`
      }));
      setClassSectionOptions(options);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Không thể tải danh sách lớp học phần';
      message.error(messageText);
      setClassSectionOptions([]);
    } finally {
      setClassSectionLoading(false);
    }
  }, []);

  const loadPracticeGroups = useCallback(async (courseId: string, subjectId: string) => {
    if (!courseId || !subjectId) {
      setPracticeGroupOptions([]);
      return;
    }
    setPracticeGroupLoading(true);
    try {
      const response = await nhomThucHanhService.getPracticeGroups({ khoaHocId: courseId, monHocId: subjectId });
      const data = Array.isArray(response?.data) ? response.data : [];
      const options = data.map((group: any) => {
        const value = String(group.MaNhomThucHanh || group.PracticeGroupID || '').trim();
        const classSectionId = group.LopHocPhanID || group.LopHocPhanId || group.lopHocPhanId || group.MaLHP || group.ClassSectionId;
        return {
          value,
          label: `${group.MaNhomThucHanh || group.PracticeGroupID} - ${group.TenNhomThucHanh || group.TenNhom || group.GroupName || ''}`.trim(),
          classSectionId: classSectionId ? String(classSectionId) : undefined,
          raw: group
        } as PracticeGroupOption;
      });
      setPracticeGroupOptions(options);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Không thể tải danh sách nhóm thực hành';
      message.error(messageText);
      setPracticeGroupOptions([]);
    } finally {
      setPracticeGroupLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeachers().catch(() => undefined);
  }, [loadTeachers]);

  useEffect(() => {
    if (selectedTeacherId) {
      fetchSchedules(weekDate, selectedTeacherId).catch(() => undefined);
    } else {
      setSchedules([]);
      setRange(undefined);
    }
  }, [selectedTeacherId, weekDate, fetchSchedules]);

  useEffect(() => {
    if (!modalVisible) {
      return;
    }
    if (!courseOptions.length) {
      loadCourses();
    }
    if (!roomOptions.length) {
      loadRooms();
    }
    if (selectedCourseId && selectedSubjectId && !practiceGroupOptions.length) {
      loadPracticeGroups(selectedCourseId, selectedSubjectId);
    }
  }, [modalVisible, courseOptions.length, roomOptions.length, practiceGroupOptions.length, selectedCourseId, selectedSubjectId, loadCourses, loadRooms, loadPracticeGroups]);

  useEffect(() => {
    if (!modalVisible || !editingRecord) {
      return;
    }
    if (editingRecord.khoaHocId) {
      loadSubjects(editingRecord.khoaHocId);
    }
    if (editingRecord.monHocId) {
      loadClassSections(editingRecord.monHocId);
    }
    if (editingRecord.khoaHocId && editingRecord.monHocId) {
      loadPracticeGroups(editingRecord.khoaHocId, editingRecord.monHocId);
    }
  }, [modalVisible, editingRecord, loadSubjects, loadClassSections, loadPracticeGroups]);

  const handleWeekChange = (value: Dayjs | null) => {
    setWeekDate(value ?? dayjs());
  };

  const handleResetToCurrentWeek = () => {
    const now = dayjs();
    setWeekDate(now);
    fetchSchedules(now, selectedTeacherId).catch(() => undefined);
  };

  const handleRefresh = () => {
    fetchSchedules(weekDate, selectedTeacherId).catch(() => undefined);
  };

  const handleCourseChange = (value?: string) => {
    form.setFieldsValue({ monHocId: undefined, lopHocPhanId: undefined, nhomThucHanhId: undefined });
    setSubjectOptions([]);
    setClassSectionOptions([]);
    setPracticeGroupOptions([]);
    if (value) {
      loadSubjects(value);
    }
  };

  const handleSubjectChange = (value?: string) => {
    form.setFieldsValue({ lopHocPhanId: undefined, nhomThucHanhId: undefined });
    setClassSectionOptions([]);
    setPracticeGroupOptions([]);
    if (value) {
      loadClassSections(value);
      if (selectedCourseId) {
        loadPracticeGroups(selectedCourseId, value);
      }
    }
  };

  const handleTeacherSelect = (value?: string) => {
    setSelectedTeacherId(value);
  };

  const handleLessonTypeChange = (value: LessonType) => {
    if (value === 'theory') {
      form.setFieldsValue({ nhomThucHanhId: undefined });
    } else {
      form.setFieldsValue({ lopHocPhanId: undefined });
    }
    form.setFieldsValue({ lessonType: value });
  };

  const handlePracticeGroupChange = useCallback((value?: string) => {
    if (!value) {
      form.setFieldsValue({ lopHocPhanId: undefined });
      return;
    }
    const selected = practiceGroupOptions.find((option) => option.value === value);
    const classSectionId = selected?.classSectionId
      ?? selected?.raw?.LopHocPhanID
      ?? selected?.raw?.LopHocPhanId
      ?? selected?.raw?.lopHocPhanId
      ?? selected?.raw?.MaLHP
      ?? selected?.raw?.ClassSectionId
      ?? null;
    if (!classSectionId) {
      console.warn('Practice group without class section', selected);
    }
    form.setFieldsValue({ lopHocPhanId: classSectionId ? String(classSectionId) : undefined });
  }, [form, practiceGroupOptions]);

  useEffect(() => {
    if (lessonType !== 'practice') {
      return;
    }
    const currentGroup = form.getFieldValue('nhomThucHanhId');
    if (currentGroup) {
      handlePracticeGroupChange(currentGroup);
    }
  }, [lessonType, practiceGroupOptions, form, handlePracticeGroupChange]);

  const openCreateModal = useCallback(() => {
    form.resetFields();
    setSubjectOptions([]);
    setClassSectionOptions([]);
    setPracticeGroupOptions([]);
    form.setFieldsValue({
      teacherId: selectedTeacherId,
      ngayGiang: weekDate.startOf('day'),
      buoiGiang: 'Sáng',
      isLocked: false,
      lessonType: 'theory'
    });
    setModalState({ visible: true, mode: 'create', record: null });
  }, [form, weekDate, selectedTeacherId]);

  const openEditModal = useCallback((record: TeacherWeeklySchedule) => {
    form.resetFields();
    setSubjectOptions([]);
    setClassSectionOptions([]);
    setPracticeGroupOptions([]);

    const lessonTypeValue: LessonType = record.nhomThucHanhId ? 'practice' : 'theory';

    form.setFieldsValue({
      teacherId: record.teacherId,
      ngayGiang: record.ngayGiang ? dayjs(record.ngayGiang) : undefined,
      buoiGiang: record.buoiGiang,
      buoiSo: record.buoiSo,
      khoaHocId: record.khoaHocId,
      monHocId: record.monHocId,
      lopHocPhanId: record.lopHocPhanId,
      nhomThucHanhId: record.nhomThucHanhId,
      phongHocId: record.phongHocId,
      ghiChu: record.ghiChu,
      isLocked: record.isLocked,
      lessonType: lessonTypeValue
    });

    setModalState({ visible: true, mode: 'edit', record });
  }, [form]);

  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, visible: false, record: null, mode: 'create' }));
    form.resetFields();
    setSubjectOptions([]);
    setClassSectionOptions([]);
    setPracticeGroupOptions([]);
  }, [form]);

  const handleModalSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const lopHocPhanIdValue = values.lopHocPhanId ? String(values.lopHocPhanId) : undefined;
      const currentLessonType: LessonType = values.lessonType ?? lessonType;
      const teacherIdValue = isEditMode ? editingRecord?.teacherId : (values.teacherId ? String(values.teacherId) : undefined);

      if (!teacherIdValue) {
        message.error('Vui lòng chọn giảng viên');
        return;
      }

      if (currentLessonType === 'theory' && !lopHocPhanIdValue) {
        message.error('Không xác định được lớp học phần cho buổi giảng này');
        return;
      }

      if (currentLessonType === 'practice' && !values.nhomThucHanhId) {
        message.error('Vui lòng chọn nhóm thực hành');
        return;
      }

      setSubmitLoading(true);

      const basePayload = {
        ngayGiang: values.ngayGiang.format('YYYY-MM-DD'),
        buoiGiang: values.buoiGiang,
        buoiSo: Number(values.buoiSo),
        khoaHocId: values.khoaHocId,
        monHocId: values.monHocId,
        lopHocPhanId: lopHocPhanIdValue,
        phongHocId: values.phongHocId,
        nhomThucHanhId: currentLessonType === 'practice' ? values.nhomThucHanhId : undefined,
        ghiChu: values.ghiChu || undefined,
        isLocked: values.isLocked || false
      };

      if (isEditMode && editingRecord) {
        await teacherWeeklyScheduleService.update(editingRecord.id, basePayload);
        message.success('Đã cập nhật lịch giảng');
      } else {
        await teacherWeeklyScheduleService.create({
          teacherId: teacherIdValue,
          ...basePayload
        });
        message.success('Đã thêm lịch giảng mới');
      }

      closeModal();
      fetchSchedules(weekDate, selectedTeacherId ?? teacherIdValue).catch(() => undefined);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      const messageText = error instanceof Error ? error.message : isEditMode ? 'Không thể cập nhật lịch giảng' : 'Không thể tạo lịch giảng';
      message.error(messageText);
    } finally {
      setSubmitLoading(false);
    }
  }, [form, lessonType, isEditMode, editingRecord, closeModal, fetchSchedules, weekDate, selectedTeacherId]);

  const handleNavigateToAttendance = useCallback(async (record: TeacherWeeklySchedule) => {
    setActivatingId(record.id);
    try {
      const activation = await teacherWeeklyScheduleService.activate(record.id);
      const targetPath = record.nhomThucHanhId ? '/attendance/practice' : '/attendance/theory';
      navigate(targetPath, {
        state: {
          schedulePrefill: {
            scheduleId: record.id,
            ...activation,
            tenKhoaHoc: record.tenKhoaHoc,
            tenMonHoc: record.tenMonHoc,
            tenLopHP: record.tenLopHP,
            tenPhongHoc: record.tenPhongHoc,
            tenNhomThucHanh: activation.tenNhomThucHanh ?? record.tenNhomThucHanh,
            nhomThucHanhId: activation.nhomThucHanhId ?? record.nhomThucHanhId,
          }
        }
      });
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Không thể kích hoạt buổi giảng';
      message.error(messageText);
    } finally {
      setActivatingId(null);
    }
  }, [navigate]);

  const handleDeleteSchedule = useCallback(async (record: TeacherWeeklySchedule) => {
    setDeletingId(record.id);
    try {
      await teacherWeeklyScheduleService.remove(record.id);
      message.success('Đã xóa lịch giảng');
      const teacherIdForRefresh = selectedTeacherId || record.teacherId;
      fetchSchedules(weekDate, teacherIdForRefresh).catch(() => undefined);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Không thể xóa lịch giảng';
      message.error(messageText);
    } finally {
      setDeletingId(null);
    }
  }, [fetchSchedules, weekDate, selectedTeacherId]);

  const sessionSelectOptions = useMemo(() => ([
    { label: 'Sáng', value: 'Sáng' },
    { label: 'Chiều', value: 'Chiều' },
    { label: 'Tối', value: 'Tối' }
  ]), []);

  const columns: ColumnsType<TeacherWeeklySchedule> = useMemo(() => ([
    {
      title: 'Ngày giảng',
      dataIndex: 'ngayGiang',
      key: 'ngayGiang',
      width: 160,
      sorter: (a, b) => dayjs(a.ngayGiang).valueOf() - dayjs(b.ngayGiang).valueOf(),
      render: (value: string) => value ? dayjs(value).format('DD/MM/YYYY') : '',
    },
    {
      title: 'Buổi',
      dataIndex: 'buoiGiang',
      key: 'buoiGiang',
      width: 120,
      filters: sessionSelectOptions.map(({ label, value }) => ({ text: label, value })),
      onFilter: (value, record) => record.buoiGiang === value,
    },
    {
      title: 'Buổi số',
      dataIndex: 'buoiSo',
      key: 'buoiSo',
      width: 100,
      sorter: (a, b) => (a.buoiSo ?? 0) - (b.buoiSo ?? 0),
      align: 'center',
    },
    {
      title: 'Giảng viên',
      dataIndex: 'teacherId',
      key: 'teacherId',
      width: 220,
      render: (value: string) => teacherLabelMap[value] || value || '-',
    },
    {
      title: 'Khóa học',
      dataIndex: 'tenKhoaHoc',
      key: 'tenKhoaHoc',
      width: 200,
      render: (_: string, record) => record.tenKhoaHoc || record.khoaHocId || '-',
    },
    {
      title: 'Môn học',
      dataIndex: 'tenMonHoc',
      key: 'tenMonHoc',
      width: 220,
      render: (_: string, record) => record.tenMonHoc || record.monHocId || '-',
    },
    {
      title: 'Lớp học phần',
      dataIndex: 'tenLopHP',
      key: 'tenLopHP',
      width: 220,
      render: (_: string, record) => record.tenLopHP || record.lopHocPhanId || '-',
    },
    {
      title: 'Nhóm thực hành',
      dataIndex: 'tenNhomThucHanh',
      key: 'tenNhomThucHanh',
      width: 220,
      render: (_: string, record) => record.tenNhomThucHanh || record.nhomThucHanhId || '-',
    },
    {
      title: 'Phòng học',
      dataIndex: 'tenPhongHoc',
      key: 'tenPhongHoc',
      width: 160,
      render: (_: string, record) => record.tenPhongHoc || record.phongHocId || '-',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'ghiChu',
      key: 'ghiChu',
      ellipsis: true,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isLocked',
      key: 'isLocked',
      width: 140,
      align: 'center',
      render: (value: boolean) => value ? <Tag color="red">Đã khóa</Tag> : <Tag color="green">Có thể chỉnh sửa</Tag>,
      filters: [
        { text: 'Đã khóa', value: true },
        { text: 'Có thể chỉnh sửa', value: false },
      ],
      onFilter: (value, record) => record.isLocked === value,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 220,
      render: (_, record) => {
        const today = dayjs();
        const isToday = dayjs(record.ngayGiang).isSame(today, 'day');
        const attendanceDisabled = record.isLocked || !isToday;
        const editDisabled = record.isLocked;

        const editButton = (
          <Button type="link" onClick={() => openEditModal(record)} disabled={editDisabled}>
            Sửa
          </Button>
        );

        const attendanceButton = (
          <Button
            type="link"
            onClick={() => handleNavigateToAttendance(record)}
            disabled={attendanceDisabled}
            loading={activatingId === record.id}
          >
            Điểm danh
          </Button>
        );

        const editControl = editDisabled ? (
          <Tooltip title="Buổi giảng đã bị khóa">
            <span>{editButton}</span>
          </Tooltip>
        ) : editButton;

        const attendanceTooltip = record.isLocked
          ? 'Buổi giảng đã bị khóa'
          : 'Chỉ mở điểm danh trong ngày giảng';

        const attendanceControl = attendanceDisabled ? (
          <Tooltip title={attendanceTooltip}>
            <span>{attendanceButton}</span>
          </Tooltip>
        ) : attendanceButton;

        const controls: React.ReactNode[] = [editControl, attendanceControl];

        if (isAdminUser) {
          const deleteControl = (
            <Popconfirm
              title="Xóa lịch giảng"
              description="Bạn có chắc chắn muốn xóa buổi giảng này? Thao tác không thể hoàn tác."
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true, loading: deletingId === record.id }}
              onConfirm={() => handleDeleteSchedule(record)}
              trigger={['click']}
            >
              <Button type="link" danger loading={deletingId === record.id}>
                Xóa
              </Button>
            </Popconfirm>
          );
          controls.push(deleteControl);
        }

        return (
          <Space size={4}>
            {controls.map((control, index) => (
              <React.Fragment key={index}>{control}</React.Fragment>
            ))}
          </Space>
        );
      }
    },
  ]), [sessionSelectOptions, teacherLabelMap, activatingId, handleNavigateToAttendance, openEditModal, isAdminUser, deletingId, handleDeleteSchedule]);

  const rangeLabel = useMemo(() => {
    if (!range) {
      return '';
    }
    const startLabel = range.start ? dayjs(range.start).format('DD/MM/YYYY') : '';
    const endLabel = range.end ? dayjs(range.end).format('DD/MM/YYYY') : '';
    return startLabel && endLabel ? `${startLabel} → ${endLabel}` : '';
  }, [range]);

  const selectedTeacherLabel = selectedTeacherId ? teacherLabelMap[selectedTeacherId] : undefined;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Space size={12}>
            <CalendarOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div>
              <Title level={3} style={{ margin: 0 }}>Lịch giảng tuần</Title>
              <Text type="secondary">Quản lý lịch dạy, phòng học và trạng thái khoá cho từng giảng viên</Text>
            </div>
          </Space>
          <Space size={8} wrap>
            <Select
              style={{ minWidth: 260 }}
              placeholder="Chọn giảng viên"
              options={teacherOptions}
              loading={teacherLoading}
              showSearch
              optionFilterProp="label"
              value={selectedTeacherId}
              onChange={handleTeacherSelect}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Thêm lịch giảng
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
              Tải lại
            </Button>
            <Button onClick={handleResetToCurrentWeek} disabled={weekDate.isSame(dayjs(), 'week')}>
              Tuần hiện tại
            </Button>
            <DatePicker
              value={weekDate}
              onChange={handleWeekChange}
              picker="week"
              format="[Tuần] WW/YYYY"
              allowClear={false}
            />
          </Space>
        </div>

        <Card>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space size={8} style={{ justifyContent: 'space-between', width: '100%', flexWrap: 'wrap' }}>
              <Text strong>Tuần hiện tại: {rangeLabel || 'Đang xác định...'}</Text>
              <Text type="secondary">
                {selectedTeacherLabel ? `Giảng viên: ${selectedTeacherLabel}` : 'Chọn giảng viên để xem lịch'} · Tổng số buổi: {schedules.length}
              </Text>
            </Space>
            <Table<TeacherWeeklySchedule>
              rowKey={(record) => `${record.id}`}
              columns={columns}
              dataSource={schedules}
              loading={loading}
              pagination={false}
              scroll={{ x: 1280 }}
              locale={{ emptyText: selectedTeacherId ? 'Không có lịch giảng trong tuần đã chọn' : 'Vui lòng chọn giảng viên để xem lịch' }}
            />
          </Space>
        </Card>

        <Modal
          title={isEditMode ? 'Chỉnh sửa lịch giảng' : 'Thêm lịch giảng'}
          open={modalVisible}
          onCancel={closeModal}
          onOk={handleModalSubmit}
          okText={isEditMode ? 'Cập nhật' : 'Lưu lịch'}
          cancelText="Hủy"
          confirmLoading={submitLoading}
          width={680}
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label="Giảng viên"
              name="teacherId"
              rules={[{ required: true, message: 'Vui lòng chọn giảng viên' }]}
            >
              <Select
                options={teacherOptions}
                loading={teacherLoading}
                showSearch
                optionFilterProp="label"
                placeholder="Chọn giảng viên"
                disabled={isEditMode}
              />
            </Form.Item>

            <Form.Item
              label="Ngày giảng"
              name="ngayGiang"
              rules={[{ required: true, message: 'Vui lòng chọn ngày giảng' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Space size={16} style={{ width: '100%' }} wrap>
              <Form.Item
                label="Buổi"
                name="buoiGiang"
                rules={[{ required: true, message: 'Vui lòng chọn buổi' }]}
                style={{ flex: 1, minWidth: 160 }}
              >
                <Select options={sessionSelectOptions} />
              </Form.Item>
              <Form.Item
                label="Buổi số"
                name="buoiSo"
                rules={[{ required: true, message: 'Vui lòng nhập buổi số' }]}
                style={{ width: 160 }}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Space>

            <Form.Item
              label="Khóa học"
              name="khoaHocId"
              rules={[{ required: true, message: 'Vui lòng chọn khóa học' }]}
            >
              <Select
                options={courseOptions}
                loading={courseLoading}
                showSearch
                optionFilterProp="label"
                placeholder="Chọn khóa học"
                onChange={handleCourseChange}
              />
            </Form.Item>

            <Form.Item
              label="Môn học"
              name="monHocId"
              rules={[{ required: true, message: 'Vui lòng chọn môn học' }]}
            >
              <Select
                options={subjectOptions}
                loading={subjectLoading}
                showSearch
                optionFilterProp="label"
                placeholder="Chọn môn học"
                onChange={handleSubjectChange}
                disabled={!selectedCourseId}
              />
            </Form.Item>

            <Form.Item label="Hình thức" name="lessonType" initialValue="theory">
              <Radio.Group onChange={(e) => handleLessonTypeChange(e.target.value)} value={lessonType}>
                <Radio.Button value="theory">Lý thuyết</Radio.Button>
                <Radio.Button value="practice">Thực hành</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label="Lớp học phần"
              name="lopHocPhanId"
              rules={lessonType === 'theory' ? [{ required: true, message: 'Vui lòng chọn lớp học phần' }] : []}
            >
              <Select
                options={classSectionOptions}
                loading={classSectionLoading}
                showSearch
                optionFilterProp="label"
                placeholder={lessonType === 'practice' ? 'Tự động xác định theo nhóm thực hành' : 'Chọn lớp học phần'}
                disabled={!selectedSubjectId || lessonType !== 'theory'}
              />
            </Form.Item>

            <Form.Item
              label="Nhóm thực hành"
              name="nhomThucHanhId"
              tooltip="Không bắt buộc đối với buổi lý thuyết. Chọn khi buổi giảng dành cho nhóm thực hành."
              rules={lessonType === 'practice' ? [{ required: true, message: 'Vui lòng chọn nhóm thực hành' }] : []}
            >
              <Select
                options={practiceGroupOptions}
                loading={practiceGroupLoading}
                showSearch
                allowClear
                optionFilterProp="label"
                placeholder={practiceGroupOptions.length ? 'Chọn nhóm thực hành (nếu có)' : 'Không có nhóm thực hành phù hợp'}
                disabled={!selectedSubjectId || lessonType !== 'practice'}
                onChange={handlePracticeGroupChange}
              />
            </Form.Item>

            <Form.Item
              label="Phòng học"
              name="phongHocId"
              rules={[{ required: true, message: 'Vui lòng chọn phòng học' }]}
            >
              <Select
                options={roomOptions}
                loading={roomLoading}
                showSearch
                optionFilterProp="label"
                placeholder="Chọn phòng học"
              />
            </Form.Item>

            <Form.Item label="Ghi chú" name="ghiChu">
              <Input.TextArea rows={3} placeholder="Thông tin thêm cho buổi học (không bắt buộc)" />
            </Form.Item>

            <Form.Item label="Khóa buổi học" name="isLocked" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      </Space>
    </div>
  );
};

export default TeacherWeeklySchedulePage;
